import { Box, Circle, Flex, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React from 'react';

import { colors } from 'packages/common-frontend/colors';
import { BasicTable } from 'packages/common-frontend/components/TanStack/BasicTable';
import { Typography } from 'packages/common-frontend/components/Typography';

// -- Summary card data (hardcoded for scaffolding) --

const SUMMARY_CARDS = [
  { label: 'TOTAL CURRENT PRINCIPAL', value: '$1.73B' },
  { label: 'ACTIVE DEALS', value: '6' },
  { label: 'WTD AVG 31+ DPD', value: '3.6%', valueColor: colors.orange[400] },
  { label: 'CERTS PENDING', value: '1', subtitle: 'Apex Industries' },
] as const;

// -- Deals table data (hardcoded for scaffolding) --

type DealStatus = 'current' | 'warning' | 'overdue';

type DealRow = {
  status: DealStatus;
  servicer: string;
  assetClass: string;
  currentPrincipal: string;
  advancesLoans: string;
  dpd31Plus: string;
  dpdChange: string;
  dpdDirection: 'up' | 'down' | 'flat';
  cycle: string;
  cycleStatus: 'complete' | 'overdue';
  certificate: string;
};

const DEALS_DATA: DealRow[] = [
  {
    status: 'current',
    servicer: 'Forward Financing',
    assetClass: 'Small Business · RBF',
    currentPrincipal: '$200.0M',
    advancesLoans: '10,000',
    dpd31Plus: '7.9%',
    dpdChange: '0.4',
    dpdDirection: 'up',
    cycle: 'MAR',
    cycleStatus: 'complete',
    certificate: 'Signed Apr 16',
  },
  {
    status: 'current',
    servicer: 'Theorem Capital',
    assetClass: 'Consumer Unsecured',
    currentPrincipal: '$428.1M',
    advancesLoans: '5,812',
    dpd31Plus: '1.8%',
    dpdChange: '0.1',
    dpdDirection: 'down',
    cycle: 'APR',
    cycleStatus: 'complete',
    certificate: 'Signed Apr 16',
  },
  {
    status: 'warning',
    servicer: 'Pinnacle Lending',
    assetClass: 'Student Loan',
    currentPrincipal: '$287.9M',
    advancesLoans: '3,945',
    dpd31Plus: '2.6%',
    dpdChange: '0.3',
    dpdDirection: 'up',
    cycle: 'APR',
    cycleStatus: 'complete',
    certificate: 'Signed Apr 14',
  },
  {
    status: 'current',
    servicer: 'Horizon Finance',
    assetClass: 'Consumer Auto',
    currentPrincipal: '$356.2M',
    advancesLoans: '4,780',
    dpd31Plus: '1.4%',
    dpdChange: '0.1',
    dpdDirection: 'down',
    cycle: 'APR',
    cycleStatus: 'complete',
    certificate: 'Signed Apr 18',
  },
  {
    status: 'overdue',
    servicer: 'Apex Industries',
    assetClass: 'Equipment Finance',
    currentPrincipal: '$198.6M',
    advancesLoans: '2,890',
    dpd31Plus: '5.1%',
    dpdChange: '1.2',
    dpdDirection: 'up',
    cycle: 'APR',
    cycleStatus: 'overdue',
    certificate: 'Blocked',
  },
  {
    status: 'current',
    servicer: 'Sterling Credit',
    assetClass: 'Consumer Unsecured',
    currentPrincipal: '$256.8M',
    advancesLoans: '3,246',
    dpd31Plus: '2.2%',
    dpdChange: '',
    dpdDirection: 'flat',
    cycle: 'APR',
    cycleStatus: 'complete',
    certificate: 'Signed Apr 12',
  },
];

// -- Column definitions --

const STATUS_COLORS: Record<DealStatus, string> = {
  current: colors.green[300],
  warning: colors.orange[300],
  overdue: colors.red[300],
};

const columnHelper = createColumnHelper<DealRow>();

function StatusCell({ status }: { status: DealStatus }): React.ReactElement {
  return <Circle size="10px" bg={STATUS_COLORS[status]} />;
}

function ServicerCell({ value }: { value: string }): React.ReactElement {
  return (
    <Text fontWeight={500} color={colors.blue[400]}>
      {value}
    </Text>
  );
}

function DpdCell({ row }: { row: DealRow }): React.ReactElement {
  const dpdColor =
    row.dpdDirection === 'up' ? colors.orange[400] : colors.green[400];
  return (
    <Flex alignItems="center" gap="4px">
      <Text color={dpdColor} fontWeight={500}>
        {row.dpd31Plus}
      </Text>
      {row.dpdChange && (
        <Text color={dpdColor} fontSize="0.75rem">
          {row.dpdDirection === 'up' ? '▲' : '▼'} {row.dpdChange}
        </Text>
      )}
      {row.dpdDirection === 'flat' && (
        <Text color={colors.gray[400]} fontSize="0.75rem">
          —
        </Text>
      )}
    </Flex>
  );
}

function CycleCell({ row }: { row: DealRow }): React.ReactElement {
  const isOverdue = row.cycleStatus === 'overdue';
  return (
    <Text
      fontSize="0.75rem"
      fontWeight={600}
      color={isOverdue ? colors.red[400] : colors.green[400]}
    >
      {row.cycle} {isOverdue ? 'TAPE OVERDUE' : '✓ COMPLETE'}
    </Text>
  );
}

function CertificateCell({ value }: { value: string }): React.ReactElement {
  const isBlocked = value === 'Blocked';
  return (
    <Flex alignItems="center" gap="4px">
      <Text color={isBlocked ? colors.red[400] : undefined}>{value}</Text>
      {!isBlocked && <Text color={colors.green[400]}>✓</Text>}
    </Flex>
  );
}

/* eslint-disable react/display-name */
const columns = [
  columnHelper.accessor('status', {
    header: '',
    size: 40,
    cell: (info) => <StatusCell status={info.getValue()} />,
  }),
  columnHelper.accessor('servicer', {
    header: 'Servicer',
    size: 180,
    cell: (info) => <ServicerCell value={info.getValue()} />,
  }),
  columnHelper.accessor('assetClass', {
    header: 'Asset Class',
    size: 180,
  }),
  columnHelper.accessor('currentPrincipal', {
    header: 'Current Principal',
    size: 150,
  }),
  columnHelper.accessor('advancesLoans', {
    header: 'Advances/Loans',
    size: 140,
  }),
  columnHelper.accessor('dpd31Plus', {
    header: '31+ DPD',
    size: 120,
    cell: (info) => <DpdCell row={info.row.original} />,
  }),
  columnHelper.accessor('cycle', {
    header: 'Cycle',
    size: 160,
    cell: (info) => <CycleCell row={info.row.original} />,
  }),
  columnHelper.accessor('certificate', {
    header: 'Certificate',
    size: 140,
    cell: (info) => <CertificateCell value={info.getValue()} />,
  }),
];
/* eslint-enable react/display-name */

// -- Component --

export const PortfolioView = (): React.ReactElement => {
  const table = useReactTable({
    data: DEALS_DATA,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Flex
      direction="column"
      padding={['1.5rem', '1.5rem', '1.5rem', '2.5rem']}
      gap="2rem"
    >
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="baseline">
        <Typography variant="page-title" trim>
          Portfolio Overview
        </Typography>
        <Text color={colors.gray[500]} fontSize="0.85rem">
          Latest cycle data · as of May 5, 2026
        </Text>
      </Flex>

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} gap="1rem">
        {SUMMARY_CARDS.map((card) => (
          <Box
            key={card.label}
            borderWidth="1px"
            borderRadius="md"
            borderColor={colors.gray[100]}
            p="1.5rem"
          >
            <VStack align="flex-start" spacing="0.25rem">
              <Text
                color={colors.gray[500]}
                fontSize="0.75rem"
                fontWeight={600}
                letterSpacing="0.02em"
              >
                {card.label}
              </Text>
              <Text
                fontWeight={600}
                fontSize="1.75rem"
                color={'valueColor' in card ? card.valueColor : undefined}
              >
                {card.value}
              </Text>
              {'subtitle' in card && (
                <Text color={colors.gray[500]} fontSize="0.8rem">
                  {card.subtitle}
                </Text>
              )}
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Readiness Banner */}
      <Box
        borderWidth="1px"
        borderColor={colors.green[200]}
        borderRadius="md"
        bg={colors.green[50]}
        px="1.5rem"
        py="0.75rem"
      >
        <Flex alignItems="center" gap="0.75rem">
          <Text color={colors.green[400]} fontSize="1.25rem">
            ✓
          </Text>
          <Text fontWeight={600}>Backup Readiness: 5 of 6 deals current</Text>
          <Text color={colors.gray[500]} fontSize="0.85rem">
            All certificates issued for April
          </Text>
          <Text color={colors.orange[400]} fontSize="0.85rem">
            · 1 tape overdue (Apex)
          </Text>
        </Flex>
      </Box>

      {/* Deals Table */}
      <Box>
        <Typography variant="section-title">
          All Deals — Latest Cycle
        </Typography>
        <BasicTable table={table} maxHeight="none" />
      </Box>
    </Flex>
  );
};
